import React, { useRef, useState } from 'react';
import { useBilling } from '../../contexts/BillingContext';
import { formatMoney, newClientKey, todayLusaka } from '../../utils/billingMoney';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
type Props = {
  sourceType: 'payment' | 'creditNote';
  sourceId: string;
  sourceNumber: string;
  accountName: string;
  available: number;
  onClose: () => void;
  onDone: (message?: string) => void;
};

/** Authorised refund of unallocated credit. Only money not applied to invoices can be refunded. */
export function RefundModal({
  sourceType,
  sourceId,
  sourceNumber,
  accountName,
  available,
  onClose,
  onDone
}: Props) {
  const {
    config,
    recordRefund
  } = useBilling();
  const key = useRef(newClientKey());
  const methods = config.paymentMethods.filter(m => m.enabled);
  const [amount, setAmount] = useState(String(available));
  const [methodId, setMethodId] = useState(methods[0]?.id ?? '');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayLusaka());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const submit = async () => {
    const method = methods.find(m => m.id === methodId);
    if (!method) return setError('Choose how the refund is paid out.');
    setBusy(true);
    setError('');
    const result = await recordRefund({
      id: `rf-${key.current}`,
      sourceType,
      sourceId,
      amount: Number(amount),
      method,
      reference,
      reason,
      date
    });
    setBusy(false);
    if (!result.ok) return setError(result.error ?? 'The refund was not recorded.');
    onDone(result.message);
  };
  return <Dialog title={`Refund from ${sourceNumber}`} subtitle={`${accountName} · ${fmt(available)} unallocated`} onClose={onClose} footer={<>{error && <p role="alert" className="mr-auto text-sm text-red-700" data-id="element-4433" data-mp="1cp24zv259s08">{error}</p>}<Button variant="outline" onClick={onClose} data-id="element-4434" data-mp="1cnhdkeb4mp6l">Cancel</Button><Button variant="danger" onClick={submit} isLoading={busy} data-id="element-4435" data-mp="3679erutlaqzm">Record refund</Button></>} data-id="element-4432" data-mp="3eirnkl2u7o3j">
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-4436" data-mp="3b02m8qgg0dfe">
        <div data-id="element-4437" data-mp="3b0vlz7gzi2o1"><label htmlFor="rf-amount" className="text-sm font-medium text-slate-700" data-id="element-4438" data-mp="3b0vhov9ybgi8">Amount</label><input id="rf-amount" type="number" min="0.01" step="0.01" max={available} value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums" data-id="element-4439" data-mp="2x5jh47946vte" /></div>
        <div data-id="element-4440" data-mp="3ty550niede4r"><label htmlFor="rf-date" className="text-sm font-medium text-slate-700" data-id="element-4441" data-mp="3ty52afr0j012">Date</label><input id="rf-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-4442" data-mp="3fynarsmc18o0" /></div>
        <div data-id="element-4443" data-mp="24iyrpcc78tsh"><label htmlFor="rf-method" className="text-sm font-medium text-slate-700" data-id="element-4444" data-mp="24j8e6py1citf">Paid out by</label><select id="rf-method" value={methodId} onChange={e => setMethodId(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-4445" data-mp="29i1v6scj6ryc">{methods.map(m => <option key={m.id} value={m.id} data-id="element-4446" data-mp="212vut8hefiic">{m.name}</option>)}</select></div>
        <div data-id="element-4447" data-mp="1wz67emcgn6yw"><label htmlFor="rf-ref" className="text-sm font-medium text-slate-700" data-id="element-4448" data-mp="1wz6a4u3uhl2l">Reference</label><input id="rf-ref" value={reference} onChange={e => setReference(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-4449" data-mp="2ayp4mk3qgxwf" /></div>
        <div className="sm:col-span-2" data-id="element-4450" data-mp="1ibl2qnzxszov"><label htmlFor="rf-reason" className="text-sm font-medium text-slate-700" data-id="element-4451" data-mp="1iatclsp3zu5z">Reason<span className="text-red-600" data-id="element-4452" data-mp="1dcrtj7ronuui"> *</span></label><textarea id="rf-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-4453" data-mp="1s0ct4rpcnbxo" /></div>
      </div>
      <p className="mt-3 text-xs text-slate-500" data-id="element-4454" data-mp="zjscv2g5zuld">Recording a refund does not move money — pay it out first, then record it here.</p>
    </Dialog>;
}